/**
 * FelixConversationSidebar — Modernize-inspired conversation list pane.
 *
 * Design Refresh Phase 4. When toggled open from the FelixChat header,
 * this slides in from the left edge of the Felix panel, showing the
 * user's recent conversations grouped by time (Today / This Week /
 * Older). Clicking a conversation loads it into the main thread.
 *
 * Structure approximates Modernize's email app: left-pane conversation
 * list bucketed by time, each row showing a title, last-message
 * snippet, and relative timestamp. Active conversation highlighted.
 *
 * The sidebar is a collapsible panel rather than a full 3-pane split
 * because FelixChat is an overlay; a true third pane would crush the
 * thread width below usable. This gives the Modernize feel without
 * breaking the overlay form factor.
 */

import { useMemo } from "react";
import { m, useReducedMotion } from "framer-motion";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MessageSquarePlus, Search, Loader2, Pin, Trash2 } from "lucide-react";

import { conversationSessionsApi } from "@/api/services";
import type { ConversationSessionRecord } from "@/api/types";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

// Local alias so the optional ``preview`` / ``pinned`` fields stay
// additive — they're populated once the sidebar is wired to the
// richer detail endpoint, and harmless to leave undefined until then.
type ConversationSummary = ConversationSessionRecord & {
  pinned?: boolean;
  preview?: string | null;
};

export interface FelixConversationSidebarProps {
  projectId?: string;
  activeSessionId: string | null;
  onSelect: (sessionId: string) => void;
  onNewConversation: () => void;
  className?: string;
}

const SIDEBAR_EASE: [number, number, number, number] = [0.2, 0.8, 0.2, 1];

function formatRelative(iso: string | null): string {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.round((now - then) / 1000);
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.round(diffSec / 60)}m`;
  if (diffSec < 86400) return `${Math.round(diffSec / 3600)}h`;
  if (diffSec < 7 * 86400) return `${Math.round(diffSec / 86400)}d`;
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

function bucketFor(iso: string | null): "today" | "week" | "older" {
  if (!iso) return "older";
  const diffMs = Date.now() - new Date(iso).getTime();
  if (diffMs < 86_400_000) return "today";
  if (diffMs < 7 * 86_400_000) return "week";
  return "older";
}

export function FelixConversationSidebar({
  projectId,
  activeSessionId,
  onSelect,
  onNewConversation,
  className,
}: FelixConversationSidebarProps) {
  const qc = useQueryClient();
  const reducedMotion = useReducedMotion();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["felixConversationSidebar", projectId],
    queryFn: async () => {
      const res = await conversationSessionsApi.list({
        project_id: projectId,
        limit: 40,
      });
      return (res.items ?? []) as ConversationSummary[];
    },
    staleTime: 15_000,
    refetchOnWindowFocus: false,
  });

  const [today, week, older] = useMemo(() => {
    if (!data) return [[], [], []] as ConversationSummary[][];
    const t: ConversationSummary[] = [];
    const w: ConversationSummary[] = [];
    const o: ConversationSummary[] = [];
    for (const item of data) {
      const b = bucketFor(item.last_message_at);
      (b === "today" ? t : b === "week" ? w : o).push(item);
    }
    // Sort each bucket newest-first
    const byDate = (a: ConversationSummary, b: ConversationSummary) =>
      new Date(b.last_message_at ?? 0).getTime() -
      new Date(a.last_message_at ?? 0).getTime();
    t.sort(byDate);
    w.sort(byDate);
    o.sort(byDate);
    return [t, w, o];
  }, [data]);

  const handleDelete = async (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm("Delete this conversation? This cannot be undone.")) {
      return;
    }
    try {
      await conversationSessionsApi.delete(sessionId);
      qc.invalidateQueries({ queryKey: ["felixConversationSidebar"] });
    } catch {
      // fail silently — the row will reappear if invalidation didn't succeed
    }
  };

  const renderGroup = (
    label: string,
    items: ConversationSummary[],
    groupIndex: number,
  ) => {
    if (items.length === 0) return null;
    return (
      <div key={label} className="space-y-1">
        <div className="px-3 py-1 text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300">
          {label}
        </div>
        <ul className="space-y-0.5">
          {items.map((c, i) => {
            const isActive = c.session_id === activeSessionId;
            return (
              <m.li
                key={c.id}
                initial={
                  reducedMotion ? false : { opacity: 0, x: -8 }
                }
                animate={
                  reducedMotion ? undefined : { opacity: 1, x: 0 }
                }
                transition={
                  reducedMotion
                    ? undefined
                    : {
                        duration: 0.24,
                        ease: SIDEBAR_EASE,
                        delay: Math.min((groupIndex * 4 + i) * 0.02, 0.3),
                      }
                }
              >
                <button
                  type="button"
                  onClick={() => onSelect(c.session_id)}
                  className={cn(
                    "group/conv relative w-full rounded-lg px-2.5 py-2 text-left transition-colors",
                    isActive
                      ? "bg-brand-500/10 text-slate-900 dark:text-slate-100 ring-1 ring-brand-500/30"
                      : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/70 hover:text-slate-900 dark:hover:text-slate-100",
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        {c.pinned && (
                          <Pin className="h-3 w-3 shrink-0 text-brand-500" />
                        )}
                        <span
                          className={cn(
                            "truncate text-[13px] font-medium",
                            isActive && "text-slate-900 dark:text-slate-100",
                          )}
                        >
                          {c.title?.trim() || "Untitled conversation"}
                        </span>
                      </div>
                      {c.preview && (
                        <div className="mt-0.5 line-clamp-1 text-xs text-slate-600 dark:text-slate-300">
                          {c.preview}
                        </div>
                      )}
                    </div>
                    <span className="shrink-0 text-xs text-slate-600 dark:text-slate-300">
                      {formatRelative(c.last_message_at)}
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={(e) => handleDelete(c.session_id, e)}
                    className={cn(
                      "absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded",
                      "opacity-0 transition-opacity group-hover/conv:opacity-100",
                      "text-slate-500 dark:text-slate-400 hover:bg-error/10 hover:text-error",
                    )}
                    aria-label="Delete conversation"
                    title="Delete"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </button>
              </m.li>
            );
          })}
        </ul>
      </div>
    );
  };

  return (
    <div
      className={cn(
        "flex h-full w-full flex-col bg-slate-50 dark:bg-slate-800/60 backdrop-blur-sm",
        "border-r border-slate-200/70 dark:border-slate-800",
        className,
      )}
    >
      {/* Header */}
      <div className="flex shrink-0 items-center gap-2 border-b border-slate-200/70 dark:border-slate-800 px-3 py-2.5">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-500 dark:text-slate-400" />
          <Input
            type="search"
            placeholder="Search"
            className="h-8 pl-7 text-[12px]"
          />
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={onNewConversation}
          aria-label="New conversation"
          className="h-8 w-8 p-0"
        >
          <MessageSquarePlus className="h-4 w-4" />
        </Button>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto px-2 py-2 space-y-3">
        {isLoading && (
          <div className="flex items-center justify-center py-8 text-slate-500 dark:text-slate-400">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        )}
        {isError && (
          <div className="px-3 py-6 text-center text-xs text-slate-600 dark:text-slate-300">
            Unable to load conversations
          </div>
        )}
        {!isLoading && !isError && data && data.length === 0 && (
          <div className="px-3 py-8 text-center text-xs text-slate-600 dark:text-slate-300">
            No conversations yet. Start one with Felix.
          </div>
        )}
        {renderGroup("Today", today, 0)}
        {renderGroup("This week", week, 1)}
        {renderGroup("Older", older, 2)}
      </div>
    </div>
  );
}
